import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveLegalDocuments, useMyAcceptances, useAcceptDocuments } from "@/hooks/useLegalDocuments";
import { useAdminRole } from "@/hooks/useAdminRole";
import { ShieldCheck, Loader2, ScrollText, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocks the app until the signed-in user has accepted the current version
 * of every active legal document. Intended to be mounted near the root of
 * the authenticated app (e.g. inside RequireAuth or Dashboard).
 */
export default function LegalConsentGate() {
  const { user } = useAuth();
  const { data: isAdmin } = useAdminRole();
  const { data: docs, isLoading: dl } = useActiveLegalDocuments();
  const { data: acceptances, isLoading: al } = useMyAcceptances();
  const accept = useAcceptDocuments();

  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const [adminDismissed, setAdminDismissed] = useState(false);

  const outstanding = useMemo(() => {
    if (!docs || !acceptances) return [];
    return docs.filter(
      (d) => !acceptances.some((a) => a.document_slug === d.slug && a.document_version === d.version),
    );
  }, [docs, acceptances]);

  if (!user || dl || al) return null;
  if (outstanding.length === 0) return null;
  if (isAdmin && adminDismissed) return null;

  const submit = async () => {
    if (!signature.trim()) {
      toast.error("Type your full legal name to sign.");
      return;
    }
    if (!agreed) {
      toast.error("Please confirm you've read and agree.");
      return;
    }
    try {
      await accept.mutateAsync({ docs: outstanding, typed_signature: signature.trim() });
      toast.success("Welcome — agreements signed.");
    } catch (e: any) {
      toast.error(e?.message || "Could not record signature.");
    }
  };

  const fullDoc = openDoc ? outstanding.find((d) => d.slug === openDoc) : null;

  return (
    <Dialog open modal>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-1.5rem)] max-h-[92vh] flex flex-col overflow-hidden p-0 gap-0"
        onPointerDownOutside={(e) => isAdmin ? setAdminDismissed(true) : e.preventDefault()}
        onEscapeKeyDown={(e) => isAdmin ? setAdminDismissed(true) : e.preventDefault()}
      >
        <div className="px-6 pt-6 pb-3 border-b border-border/50 shrink-0 relative">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-gold" />
              <DialogTitle className="font-display text-lg">Member Agreements Required</DialogTitle>
            </div>
            <DialogDescription className="text-sm">
              Before you continue, please review and sign the following. These exist so the program can keep helping you and other members in good faith.
            </DialogDescription>
          </DialogHeader>
          {isAdmin && (
            <button
              onClick={() => setAdminDismissed(true)}
              className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-md border border-gold/40 bg-gold/10 px-2.5 py-1 text-[10px] uppercase tracking-wider text-gold hover:bg-gold/20 transition"
              aria-label="Admin: skip signing"
            >
              <X className="h-3 w-3" /> Admin skip
            </button>
          )}
        </div>

        {/* Scrollable middle: document list */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <ul className="space-y-2">
            {outstanding.map((d) => (
              <li
                key={d.slug}
                className="flex items-start justify-between gap-3 rounded-md border border-border/50 bg-background/40 p-3"
              >
                <div className="min-w-0">
                  <p className="font-display text-sm font-bold text-foreground">{d.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    v{d.version} · effective {new Date(d.effective_date).toLocaleDateString()}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-8 gap-1 shrink-0" onClick={() => setOpenDoc(d.slug)}>
                  <ScrollText className="h-3 w-3" /> Read
                </Button>
              </li>
            ))}
          </ul>
        </div>

        {/* Sticky bottom: signature + submit always visible */}
        <div className="shrink-0 px-6 py-4 border-t border-border/50 space-y-3 bg-card/40">
          <div className="flex items-start gap-2">
            <Checkbox id="agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
            <Label htmlFor="agree" className="text-xs leading-snug cursor-pointer">
              I confirm I am 18+, have read each document above, and agree to be bound by them with the same force as a handwritten signature (E-SIGN Act).
            </Label>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Type your full legal name to sign
            </Label>
            <Input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="e.g. John Henry Doe"
              className="h-10 font-display"
            />
          </div>
          <Button onClick={submit} disabled={accept.isPending} className="w-full bg-gold text-primary-foreground hover:bg-gold/90 h-11">
            {accept.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</> : `Sign all ${outstanding.length} & enter`}
          </Button>
        </div>
      </DialogContent>

      {/* Nested doc reader */}
      {fullDoc && (
        <Dialog open onOpenChange={() => setOpenDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0">
            <div className="px-6 pt-6 pb-3 border-b border-border/50">
              <DialogTitle className="font-display text-lg">{fullDoc.title}</DialogTitle>
              <DialogDescription className="text-xs">
                v{fullDoc.version} · effective {new Date(fullDoc.effective_date).toLocaleDateString()}
              </DialogDescription>
            </div>
            <ScrollArea className="px-6 py-4 max-h-[70vh]">
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-foreground/90">
                {fullDoc.body_markdown}
              </pre>
            </ScrollArea>
            <div className="px-6 pb-6 pt-3 border-t border-border/50">
              <Button onClick={() => setOpenDoc(null)} className="w-full">Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
